import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useReducer, useRef, useEffect } from "react";
import { usePlatformIsActive } from "../lifecycle/PlatformLifecycleContext.js";
import { usePlatformObservability } from "../observability/PlatformObservabilityContext.js";
const INITIAL_STATE = {
    priceByOwner: {},
    orderbookByOwner: {},
    sportMatchByOwner: {},
    perpTradesByOwner: {},
    predictionTradesByOwner: {},
    predictionCommentsByOwner: {},
};
const normalizeIds = (ids) => {
    const unique = new Set();
    for (const id of ids) {
        if (typeof id !== "string")
            continue;
        const trimmed = id.trim();
        if (!trimmed)
            continue;
        unique.add(trimmed);
    }
    return Array.from(unique).sort();
};
const ownerKey = (channel) => {
    if (channel === "price")
        return "priceByOwner";
    if (channel === "orderbook")
        return "orderbookByOwner";
    if (channel === "perpTrades")
        return "perpTradesByOwner";
    if (channel === "predictionTrades")
        return "predictionTradesByOwner";
    if (channel === "predictionComments")
        return "predictionCommentsByOwner";
    return "sportMatchByOwner";
};
const reducer = (state, action) => {
    const key = ownerKey(action.channel);
    const bucket = state[key];
    if (action.type === "set") {
        const next = normalizeIds(action.ids);
        const prev = bucket[action.ownerId] ?? [];
        if (prev.length === next.length && prev.every((id, idx) => id === next[idx]))
            return state;
        return { ...state, [key]: { ...bucket, [action.ownerId]: next } };
    }
    if (!(action.ownerId in bucket))
        return state;
    const { [action.ownerId]: _removed, ...rest } = bucket;
    return { ...state, [key]: rest };
};
const LiveSubscriptionInterestsContext = createContext(null);
const aggregate = (byOwner) => {
    const acc = new Set();
    for (const ids of Object.values(byOwner)) {
        for (const id of ids)
            acc.add(id);
    }
    return Array.from(acc).sort();
};
export function LiveSubscriptionInterestsProvider({ children }) {
    const observability = usePlatformObservability();
    const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
    const tradeListenersRef = useRef(new Map());
    const commentListenersRef = useRef(new Map());
    const setInterest = useCallback((channel, ownerId, ids) => {
        dispatch({ type: "set", channel, ownerId, ids });
    }, []);
    const clearInterest = useCallback((channel, ownerId) => {
        dispatch({ type: "clear", channel, ownerId });
    }, []);
    const publishPredictionTrades = useCallback((standardizedInstrumentId, trades) => {
        const listeners = tradeListenersRef.current.get(standardizedInstrumentId);
        if (!listeners || listeners.size === 0)
            return;
        for (const fn of listeners) {
            try {
                fn(trades);
            }
            catch (error) {
                // Contain listener failures so one broken subscriber can't break the
                // pub/sub loop for the rest — but surface the error via telemetry
                // so silent swallowing doesn't hide real bugs.
                observability.error("LiveSubscriptionInterestsProvider: prediction trades listener threw", error);
            }
        }
    }, [observability]);
    const publishPredictionComments = useCallback((standardizedInstrumentId, comments) => {
        const listeners = commentListenersRef.current.get(standardizedInstrumentId);
        if (!listeners || listeners.size === 0)
            return;
        for (const fn of listeners) {
            try {
                fn(comments);
            }
            catch (error) {
                observability.error("LiveSubscriptionInterestsProvider: prediction comments listener threw", error);
            }
        }
    }, [observability]);
    const subscribePredictionComments = useCallback((standardizedInstrumentId, listener) => {
        let bucket = commentListenersRef.current.get(standardizedInstrumentId);
        if (!bucket) {
            bucket = new Set();
            commentListenersRef.current.set(standardizedInstrumentId, bucket);
        }
        bucket.add(listener);
        return () => {
            const b = commentListenersRef.current.get(standardizedInstrumentId);
            if (!b)
                return;
            b.delete(listener);
            if (b.size === 0)
                commentListenersRef.current.delete(standardizedInstrumentId);
        };
    }, []);
    const subscribePredictionTrades = useCallback((standardizedInstrumentId, listener) => {
        let bucket = tradeListenersRef.current.get(standardizedInstrumentId);
        if (!bucket) {
            bucket = new Set();
            tradeListenersRef.current.set(standardizedInstrumentId, bucket);
        }
        bucket.add(listener);
        return () => {
            const b = tradeListenersRef.current.get(standardizedInstrumentId);
            if (!b)
                return;
            b.delete(listener);
            if (b.size === 0)
                tradeListenersRef.current.delete(standardizedInstrumentId);
        };
    }, []);
    const value = useMemo(() => ({
        priceInstrumentIds: aggregate(state.priceByOwner),
        orderbookInstrumentIds: aggregate(state.orderbookByOwner),
        sportMatchInstrumentIds: aggregate(state.sportMatchByOwner),
        perpTradesInstrumentIds: aggregate(state.perpTradesByOwner),
        predictionTradesInstrumentIds: aggregate(state.predictionTradesByOwner),
        predictionCommentsInstrumentIds: aggregate(state.predictionCommentsByOwner),
        setInterest,
        clearInterest,
        publishPredictionTrades,
        subscribePredictionTrades,
        publishPredictionComments,
        subscribePredictionComments,
    }), [
        state,
        setInterest,
        clearInterest,
        publishPredictionTrades,
        subscribePredictionTrades,
        publishPredictionComments,
        subscribePredictionComments,
    ]);
    return (_jsx(LiveSubscriptionInterestsContext.Provider, { value: value, children: children }));
}
function useChannelInterest(channel, ids, enabled) {
    const isActive = usePlatformIsActive();
    const context = useContext(LiveSubscriptionInterestsContext);
    if (!context) {
        throw new Error("useChannelInterest must be used within LiveSubscriptionInterestsProvider");
    }
    const ownerId = useRef(`live-interest-${channel}-${Math.random().toString(36).slice(2)}`).current;
    const normalized = useMemo(() => normalizeIds(ids.filter((id) => Boolean(id))), [ids]);
    const normalizedKey = normalized.join("|");
    const active = enabled && isActive && normalized.length > 0;
    const { setInterest, clearInterest } = context;
    useEffect(() => {
        if (active)
            setInterest(channel, ownerId, normalized);
        else
            clearInterest(channel, ownerId);
    }, [active, channel, ownerId, normalized, normalizedKey, setInterest, clearInterest]);
    useEffect(() => () => {
        clearInterest(channel, ownerId);
    }, [channel, ownerId, clearInterest]);
}
export function useLivePriceSubscription(ids, enabled = true) {
    useChannelInterest("price", ids, enabled);
}
export function useLiveOrderbookSubscription(ids, enabled = true) {
    useChannelInterest("orderbook", ids, enabled);
}
export function useLiveSportMatchSubscription(ids, enabled = true) {
    useChannelInterest("sportMatch", ids, enabled);
}
export function useLivePerpTradesSubscription(ids, enabled = true) {
    useChannelInterest("perpTrades", ids, enabled);
}
export function useLivePredictionTradesSubscription(ids, enabled = true) {
    useChannelInterest("predictionTrades", ids, enabled);
}
export function useLivePredictionCommentsSubscription(ids, enabled = true) {
    useChannelInterest("predictionComments", ids, enabled);
}
/**
 * Listens for live prediction trades published for a given event. Listener fires
 * with each buffered batch. The consumer must ALSO call
 * useLivePredictionTradesSubscription to register interest so the backend
 * actually pushes trades to the client.
 */
export function usePredictionTradesListener(standardizedInstrumentId, listener) {
    const context = useContext(LiveSubscriptionInterestsContext);
    if (!context) {
        throw new Error("usePredictionTradesListener must be used within LiveSubscriptionInterestsProvider");
    }
    const listenerRef = useRef(listener);
    listenerRef.current = listener;
    const { subscribePredictionTrades } = context;
    useEffect(() => {
        if (!standardizedInstrumentId)
            return;
        const unsubscribe = subscribePredictionTrades(standardizedInstrumentId, (trades) => {
            listenerRef.current(trades);
        });
        return unsubscribe;
    }, [standardizedInstrumentId, subscribePredictionTrades]);
}
export function usePredictionTradesListenerForIds(standardizedInstrumentIds, listener) {
    const context = useContext(LiveSubscriptionInterestsContext);
    if (!context) {
        throw new Error("usePredictionTradesListenerForIds must be used within LiveSubscriptionInterestsProvider");
    }
    const listenerRef = useRef(listener);
    listenerRef.current = listener;
    const { subscribePredictionTrades } = context;
    const idsKey = normalizeIds(standardizedInstrumentIds.filter((id) => typeof id === "string")).join("|");
    useEffect(() => {
        const ids = idsKey.length > 0 ? idsKey.split("|") : [];
        if (ids.length === 0)
            return;
        const unsubscribeFns = ids.map((id) => subscribePredictionTrades(id, (trades) => {
            listenerRef.current(trades);
        }));
        return () => {
            for (const unsubscribe of unsubscribeFns) {
                unsubscribe();
            }
        };
    }, [idsKey, subscribePredictionTrades]);
}
export function usePredictionCommentsListener(standardizedInstrumentId, listener) {
    const context = useContext(LiveSubscriptionInterestsContext);
    if (!context) {
        throw new Error("usePredictionCommentsListener must be used within LiveSubscriptionInterestsProvider");
    }
    const listenerRef = useRef(listener);
    listenerRef.current = listener;
    const { subscribePredictionComments: sub } = context;
    useEffect(() => {
        if (!standardizedInstrumentId)
            return;
        return sub(standardizedInstrumentId, (comments) => listenerRef.current(comments));
    }, [standardizedInstrumentId, sub]);
}
export function useLivePredictionCommentsPublisher() {
    const context = useContext(LiveSubscriptionInterestsContext);
    if (!context) {
        throw new Error("useLivePredictionCommentsPublisher must be used within LiveSubscriptionInterestsProvider");
    }
    return context.publishPredictionComments;
}
/** Internal: the publisher hook used by LiveUpdatesProvider. */
export function useLivePredictionTradesPublisher() {
    const context = useContext(LiveSubscriptionInterestsContext);
    if (!context) {
        throw new Error("useLivePredictionTradesPublisher must be used within LiveSubscriptionInterestsProvider");
    }
    return context.publishPredictionTrades;
}
export function useLiveSubscriptionInterests() {
    const context = useContext(LiveSubscriptionInterestsContext);
    if (!context) {
        throw new Error("useLiveSubscriptionInterests must be used within LiveSubscriptionInterestsProvider");
    }
    return {
        priceInstrumentIds: context.priceInstrumentIds,
        orderbookInstrumentIds: context.orderbookInstrumentIds,
        sportMatchInstrumentIds: context.sportMatchInstrumentIds,
        perpTradesInstrumentIds: context.perpTradesInstrumentIds,
        predictionTradesInstrumentIds: context.predictionTradesInstrumentIds,
        predictionCommentsInstrumentIds: context.predictionCommentsInstrumentIds,
    };
}
